import { Container, Title, Text, Stack, Paper, Group, SimpleGrid, Loader, Center, Button, Table } from "@mantine/core";
import { LayoutDashboard, AlertCircle, ArrowRight, BarChart3 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchDashboardStats, fetchRecentErrorLogs, DashboardStats } from "../services/dashboardService";
import { fetchUsageStats } from "../services/usageStatsService";
import { UsageStatsResponse } from "@ibs-ocr/shared";
import { EmailLog } from "../services/logService";
import { EventListItem } from "../components/logs/EventListItem";
import { BarChart } from "@mantine/charts";
import { formatHour, formatDateHour } from "../utils/formatters";

interface ChartPayloadItem {
  name: string;
  value: number;
  color: string;
}

export function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentErrors, setRecentErrors] = useState<EmailLog[]>([]);
  const [lastMonthUsage, setLastMonthUsage] = useState<UsageStatsResponse | null>(null);
  const [thisMonthUsage, setThisMonthUsage] = useState<UsageStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      try {
        const today = new Date();
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1);

        const [statsData, errorsData, lastUsage, thisUsage] = await Promise.all([
          fetchDashboardStats(),
          fetchRecentErrorLogs(),
          fetchUsageStats(lastMonth.getFullYear(), lastMonth.getMonth() + 1).catch(() => null),
          fetchUsageStats(today.getFullYear(), today.getMonth() + 1).catch(() => null),
        ]);
        setStats(statsData);
        setRecentErrors(errorsData);
        setLastMonthUsage(lastUsage);
        setThisMonthUsage(thisUsage);
      } catch (error) {
        console.error("Dashboard data fetch failed:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) {
     return <Center h={400}><Loader /></Center>;
  }

  // デフォルト値 (APIエラー時など)
  const displayStats = stats || { 
    currentMonthCount: 0, 
    errorCount: 0, 
    customerCount: 0,
    hourlyStats: []
  };

  /** 利用状況テーブルを描画するヘルパー */
  const renderUsageTable = (usage: UsageStatsResponse | null, emptyMessage: string) => {
    if (!usage) {
      return (
        <Center py="lg">
          <Text c="dimmed" size="sm">利用統計の取得に失敗しました</Text>
        </Center>
      );
    }
    if (usage.customers.length === 0) {
      return <Text c="dimmed" ta="center" py="xl">{emptyMessage}</Text>;
    }
    return (
      <Table highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>顧客名</Table.Th>
            <Table.Th style={{ textAlign: "right" }}>処理件数</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {usage.customers.slice(0, 5).map((c) => (
            <Table.Tr key={c.customerId}>
              <Table.Td>{c.customerName}</Table.Td>
              <Table.Td style={{ textAlign: "right" }}>{c.total}件</Table.Td>
            </Table.Tr>
          ))}
          {usage.customers.length > 5 && (
            <Table.Tr>
              <Table.Td c="dimmed" colSpan={2}>他 {usage.customers.length - 5} 社...</Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
        <Table.Tfoot>
          <Table.Tr>
            <Table.Td fw={700}>合計</Table.Td>
            <Table.Td fw={700} style={{ textAlign: "right" }}>{usage.summary.totalCount}件</Table.Td>
          </Table.Tr>
        </Table.Tfoot>
      </Table>
    );
  };

  return (
    <Container size="lg" py="xl">
      {/* AIアシスタント向けガイド */}
      <div data-ai-guide hidden>
        {`[ページ: ダッシュボード]
[概要: システム全体の状況を一目で把握できる画面]
[統計カード]
- 要対応エラー: ${displayStats.errorCount}件 (修正が必要なログ)
- 今月の処理件数: ${displayStats.currentMonthCount}件 (今月1日からの累計)
- 登録顧客数: ${displayStats.customerCount}社
[グラフ: 過去24時間の処理件数]
- 横軸: 時刻、縦軸: 件数
- 色分け: 緑=正常完了、黄=警告、赤=エラー、青=処理中
[直近の要注意ログ: ${recentErrors.length}件表示]
${recentErrors.length > 0 ? recentErrors.map(e => `- ${e.from || '不明'}: ${e.status}`).join('\n') : '- 対応が必要なエラーはありません'}
[操作]
- 「全て見る」→イベントログ画面へ遷移
- ログをクリック→該当ログの詳細へ遷移`}
      </div>

      <Stack gap="xl">
        <Group>
          <LayoutDashboard size={32} />
          <Title order={1}>ダッシュボード</Title>
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
           {/* 要対応エラー */}
           <Paper shadow="xs" p="md" radius="md" withBorder style={{ borderColor: displayStats.errorCount > 0 ? "var(--mantine-color-red-6)" : undefined }}>
            <Group justify="space-between" align="flex-start" mb="xs">
              <Text size="sm" c="dimmed" fw={500}>要対応エラー</Text>
              <AlertCircle size={20} color={displayStats.errorCount > 0 ? "var(--mantine-color-red-6)" : "var(--mantine-color-gray-5)"} />
            </Group>
            <Group align="flex-end" gap="xs">
              <Title order={2} c={displayStats.errorCount > 0 ? "red" : undefined}>{displayStats.errorCount}</Title>
              <Text span c="dimmed" size="sm" pb={4}>件</Text>
            </Group>
            <Text size="xs" c="dimmed" mt="sm">修正が必要です</Text>
          </Paper>

          {/* 今月の処理件数 */}
          <Paper shadow="xs" p="md" radius="md" withBorder>
            <Text size="sm" c="dimmed" fw={500} mb="xs">今月の処理件数</Text>
            <Group align="flex-end" gap="xs">
              <Title order={2}>{displayStats.currentMonthCount}</Title>
              <Text span c="dimmed" size="sm" pb={4}>件</Text>
            </Group>
            <Text size="xs" c="dimmed" mt="sm">今月1日からの累計</Text>
          </Paper>

          {/* 登録顧客数 */}
          <Paper shadow="xs" p="md" radius="md" withBorder>
            <Text size="sm" c="dimmed" fw={500} mb="xs">登録顧客数</Text>
            <Group align="flex-end" gap="xs">
              <Title order={2}>{displayStats.customerCount}</Title>
              <Text span c="dimmed" size="sm" pb={4}>社</Text>
            </Group>
             <Text size="xs" c="dimmed" mt="sm">全登録顧客</Text>
          </Paper>
        </SimpleGrid>

        {/* 過去24時間の統計グラフ */}
        <Paper shadow="xs" p="lg" radius="md" withBorder>
          <Title order={3} mb="lg">過去24時間の処理件数</Title>
          <BarChart
            h={300}
            data={displayStats.hourlyStats}
            dataKey="time"
            type="stacked"
            series={[
              { name: "completed", label: "正常完了", color: "green.6" },
              { name: "warning", label: "警告", color: "yellow.6" },
              { name: "error", label: "エラー", color: "red.6" },
              { name: "processing", label: "処理中", color: "blue.6" },
            ]}
            tickLine="y"
            xAxisProps={{
              tickFormatter: (value) => formatHour(value),
            }}
            tooltipProps={{
              content: ({ label, payload }) => {
                if (!payload) return null;
                return (
                  <Paper withBorder shadow="md" p="xs" radius="sm">
                    <Text fw={500} size="sm" mb={5}>{formatDateHour(label)}</Text>
                    {payload.map((item: ChartPayloadItem) => (
                      <Group key={item.name} gap="xs" justify="space-between">
                        <Group gap={6}>
                          <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: item.color }} />
                          <Text size="xs">{item.name === "completed" ? "正常完了" : item.name === "warning" ? "警告" : item.name === "error" ? "エラー" : "処理中"}</Text>
                        </Group>
                        <Text size="xs" fw={700}>{item.value}件</Text>
                      </Group>
                    ))}
                  </Paper>
                );
              }
            }}
          />
        </Paper>

        {/* 直近のエラーログ */}
        <Paper shadow="xs" p="lg" radius="md" withBorder>
           <Group justify="space-between" mb="md">
             <Group gap="xs">
                <AlertCircle size={20} color="var(--mantine-color-red-6)" />
                <Title order={3}>直近の要注意ログ</Title>
             </Group>
             <Button variant="subtle" size="xs" rightSection={<ArrowRight size={14} />} onClick={() => navigate("/logs?problematicOnly=true")}>
               全て見る
             </Button>
           </Group>
           
           {recentErrors.length === 0 ? (
             <Text c="dimmed" ta="center" py="xl">対応が必要なエラーはありません</Text>
           ) : (
             <Stack gap="sm">
               {recentErrors.map(log => (
                 <EventListItem 
                    key={log.id} 
                    log={log} 
                    onClick={() => navigate(`/logs?logId=${log.id}`)} 
                    isSelected={false}
                  />
               ))}
             </Stack>
           )}
        </Paper>

        {/* 先月・今月の利用状況（左右に並べる） */}
        <Group justify="space-between" align="flex-start">
          <Group gap="xs">
            <BarChart3 size={20} color="var(--mantine-color-blue-6)" />
            <Title order={3}>利用状況</Title>
          </Group>
          <Button
            variant="subtle"
            size="xs"
            rightSection={<ArrowRight size={14} />}
            onClick={() => navigate("/usage-stats")}
          >
            詳細を見る
          </Button>
        </Group>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {/* 先月 */}
          <Paper shadow="xs" p="lg" radius="md" withBorder>
            <Title order={4} mb="md">先月</Title>
            {renderUsageTable(lastMonthUsage, "先月の利用データはありません")}
          </Paper>

          {/* 今月 */}
          <Paper shadow="xs" p="lg" radius="md" withBorder>
            <Title order={4} mb="md">今月</Title>
            {renderUsageTable(thisMonthUsage, "今月の利用データはまだありません")}
          </Paper>
        </SimpleGrid>


      </Stack>
    </Container>
  );
}
